import { Close as CloseIcon, Download as DownloadIcon } from '@mui/icons-material';
import { useLazyGetExportJobDetailQuery } from '../services/exportJobs.service';
import { EXPORT_KIND_LABELS, ExportJobStatus } from '../models/ExportJob';
import type { ExportJobDetail } from '../models/ExportJob';
import ExportJobStatusChip from './ExportJobStatusChip';
import { useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Divider,
  IconButton,
  CircularProgress,
  Button,
  Alert,
} from '@mui/material';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs} s`;
  return `${Math.floor(secs / 60)} min ${secs % 60} s`;
}

export type UseLazyExportJobDetailQuery = () => readonly [
  (jobId: string) => unknown,
  { data?: ExportJobDetail; isFetching: boolean; isError: boolean },
  ...unknown[],
];

interface Props {
  jobId: string | null;
  onClose: () => void;
  useLazyDetailQuery?: UseLazyExportJobDetailQuery;
}

const ExportJobDetailDrawer = ({
  jobId,
  onClose,
  useLazyDetailQuery = useLazyGetExportJobDetailQuery,
}: Props) => {
  const [fetchDetail, { data, isFetching, isError }] = useLazyDetailQuery();

  useEffect(() => {
    if (jobId) fetchDetail(jobId);
  }, [jobId, fetchDetail]);

  return (
    <Drawer anchor='right' open={!!jobId} onClose={onClose}>
      <Box sx={{ width: 420, p: 3, display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant='h6'>Detalle del Job</Typography>
          <IconButton onClick={onClose} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />

        {isFetching && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {isError && !isFetching && (
          <Alert severity='error'>No se pudo cargar el detalle del job.</Alert>
        )}

        {data && !isFetching && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Row label='Estado'>
                <ExportJobStatusChip status={data.status} />
              </Row>
              <Row label='Tipo'>{EXPORT_KIND_LABELS[data.exportKind] ?? data.exportKind}</Row>
              <Row label='Archivo'>{data.fileName}</Row>
              <Row label='Filas exportadas'>{data.rowCount != null ? data.rowCount.toLocaleString() : '—'}</Row>
              <Row label='Duración'>{formatDuration(data.durationMs)}</Row>
              <Row label='Creado'>{formatDate(data.createdAt)}</Row>
              <Row label='Iniciado'>{formatDate(data.startedAt)}</Row>
              <Row label='Finalizado'>{formatDate(data.finishedAt)}</Row>
            </Box>

            {data.errorMessage && (
              <Alert severity='error' sx={{ mt: 1 }}>
                <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5 }}>
                  Error
                </Typography>
                {data.errorMessage}
              </Alert>
            )}

            {data.filterPayload && Object.keys(data.filterPayload).length > 0 && (
              <Box>
                <Typography variant='subtitle2' sx={{ mb: 1, color: 'text.secondary' }}>
                  Filtros aplicados
                </Typography>
                <Box
                  component='pre'
                  sx={{
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    p: 1.5,
                    fontSize: '0.75rem',
                    overflow: 'auto',
                    maxHeight: 200,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                  }}
                >
                  {JSON.stringify(data.filterPayload, null, 2)}
                </Box>
              </Box>
            )}

            {data.status === ExportJobStatus.COMPLETED && data.downloadUrl && (
              <Box sx={{ mt: 'auto' }}>
                <Button
                  variant='contained'
                  fullWidth
                  startIcon={<DownloadIcon />}
                  href={data.downloadUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Descargar archivo
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
};

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
      <Typography variant='body2' sx={{ color: 'text.secondary', minWidth: 120 }}>
        {label}
      </Typography>
      <Typography variant='body2' sx={{ textAlign: 'right', wordBreak: 'break-word' }}>
        {children}
      </Typography>
    </Box>
  );
}

export default ExportJobDetailDrawer;
